import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, CheckCheck, MessageSquare, RefreshCw, Search, ShieldCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, fmtMinutes, fmtPhone, fmtShort, relTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, FilterSelect, LiveTimer, Loading, PageHeader, Pill, Section, Spinner } from "@/components/common";
import { cn } from "@/lib/utils";

const STATUS_META = {
  awaiting: { label: "Awaiting reply", tone: "amber" },
  no_response_needed: { label: "No reply needed", tone: "slate" },
  resolved: { label: "Resolved", tone: "emerald" },
};

function ThreadDrawer({ id, onClose }) {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["thread", id], queryFn: () => api.get(`/threads/${id}`).then((r) => r.data), enabled: !!id });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["thread", id] }); qc.invalidateQueries({ queryKey: ["threads"] }); qc.invalidateQueries({ queryKey: ["recent"] }); };
  const resolve = useMutation({ mutationFn: () => api.post(`/threads/${id}/resolve`, { note: "Handled outside OpenPhone" }).then((r) => r.data), onSuccess: () => { toast.success("Marked as handled"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const reclass = useMutation({ mutationFn: (needs) => api.post(`/threads/${id}/reclassify`, { needs_response: needs }).then((r) => r.data), onSuccess: () => { toast.success("Classification updated"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const t = q.data?.thread;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-[#0f172a] border-[#1f293d] p-0 overflow-y-auto" data-testid="thread-drawer">
        {!t ? (
          <div className="p-6"><SheetTitle className="sr-only">Loading conversation</SheetTitle><Loading /></div>
        ) : (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#1f293d] space-y-3">
              <div className="eyebrow">{t.phone_number_name || fmtPhone(t.phone_number)} line · {t.message_count} messages</div>
              <SheetTitle className="font-display text-xl text-white font-mono">{fmtPhone(t.client_number)}</SheetTitle>
              <div className="flex flex-wrap items-center gap-1.5">
                <Pill tone={STATUS_META[t.status]?.tone}>{STATUS_META[t.status]?.label || t.status}</Pill>
                {t.responsible_name ? <Pill tone="blue">Owner · {t.responsible_name}</Pill> : <Pill tone="red"><UserX className="h-3 w-3" /> Unassigned → fallback recipient</Pill>}
                {t.status === "awaiting" && <Pill tone="amber">waiting <LiveTimer since={t.awaiting_since} /></Pill>}
                {t.alert_count > 0 && <Pill tone="red"><BellRing className="h-3 w-3" /> {t.alert_count} alert{t.alert_count > 1 ? "s" : ""} sent</Pill>}
                {t.response_minutes != null && <Pill tone="emerald">{t.resolution === "call" ? "resolved by call" : "replied"} in {fmtMinutes(t.response_minutes)}</Pill>}
              </div>
              {t.classification && (
                <div className="text-xs text-slate-400 panel p-3" data-testid="thread-classification">
                  <span className="eyebrow mr-2">AI triage · {t.classification.method}</span>
                  {t.classification.needs_response ? "Reply required" : "No reply needed"} — {t.classification.reason}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {t.status === "awaiting" && (
                  <Button data-testid="thread-resolve-button" size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={resolve.isPending} onClick={() => resolve.mutate()}>
                    {resolve.isPending ? <Spinner /> : <CheckCheck className="h-3.5 w-3.5 mr-1.5" />} Mark handled
                  </Button>
                )}
                {isAdmin && (
                  <>
                    <Button data-testid="thread-mark-needs-reply" size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={reclass.isPending} onClick={() => reclass.mutate(true)}>Needs reply</Button>
                    <Button data-testid="thread-mark-no-reply" size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={reclass.isPending} onClick={() => reclass.mutate(false)}>No reply needed</Button>
                  </>
                )}
              </div>
            </SheetHeader>
            <div className="px-6 py-4 space-y-2" data-testid="thread-messages">
              {q.data.messages.map((m) => (
                <div key={m.id} className={cn("flex", m.direction === "outgoing" ? "justify-end" : "justify-start")}>
                  <div className={cn("max-w-[80%] rounded-lg px-3 py-2 text-sm border", m.direction === "outgoing" ? "bg-blue-600/15 border-blue-500/30 text-slate-100" : "bg-[#161f30] border-[#2d3d58] text-slate-200")}>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">{m.direction === "outgoing" ? m.user_name || "Team" : "Client"} · {fmtShort(m.created_at)}</div>
                    {m.text || <span className="italic text-slate-500">[media]</span>}
                  </div>
                </div>
              ))}
            </div>
            {t.alerts?.length > 0 && (
              <div className="px-6 pb-6">
                <div className="eyebrow mb-2">Alert history</div>
                <ul className="space-y-1.5">
                  {t.alerts.map((a, i) => (
                    <li key={i} className="text-xs text-slate-300 panel p-2.5 flex flex-wrap items-center gap-2">
                      <Pill tone={a.level > 1 ? "red" : "amber"}>#{a.level}</Pill>
                      <span className="font-mono text-slate-500">{fmtDateTime(a.sent_at)}</span>
                      <span>→ {a.to}{a.cc?.length ? ` · cc ${a.cc.join(", ")}` : ""}</span>
                      <Pill tone={a.email_status === "sent" ? "emerald" : "red"}>email {a.email_status}</Pill>
                      <Pill tone={a.slack_status === "sent" ? "emerald" : "slate"}>slack {a.slack_status}</Pill>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Texts() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState("awaiting");
  const [agent, setAgent] = useState("");
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const openId = params.get("thread");
  const threads = useQuery({ queryKey: ["threads", status, agent, q], queryFn: () => api.get("/threads", { params: { status, agent: agent || undefined, q: q || undefined } }).then((r) => r.data), refetchInterval: 30000 });
  const alerts = useQuery({ queryKey: ["alerts"], queryFn: () => api.get("/threads/alerts", { params: { limit: 50 } }).then((r) => r.data), enabled: status === "alerts" });
  const meta = useQuery({ queryKey: ["call-filters"], queryFn: () => api.get("/calls/filters").then((r) => r.data), enabled: isAdmin });
  const run = useMutation({ mutationFn: () => api.post("/jobs/sms-sla").then((r) => r.data), onSuccess: (d) => { toast.success(`SLA check complete · ${d.alerts_sent ?? 0} alert(s) sent${d.skipped ? ` (${d.skipped})` : ""}`); qc.invalidateQueries(); }, onError: (e) => toast.error(errMsg(e)) });
  const counts = threads.data?.counts || {};

  return (
    <>
      <PageHeader
        eyebrow="Client SMS response SLA"
        title={isAdmin ? "Text monitoring" : "My text queue"}
        description="Inbound client texts are triaged by AI — courtesy messages are auto-resolved, real questions start an SLA timer. The last team member to text the client owns the reply; unassigned threads route to the fallback contact. A text reply or a phone call to the client resolves the timer. Breaches alert via email + Slack, and repeat reminders copy the admin."
        actions={isAdmin && (
          <Button data-testid="sms-run-check-button" size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={run.isPending} onClick={() => run.mutate()}>
            <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", run.isPending && "animate-spin")} /> Run SLA check now
          </Button>
        )}
        testId="texts-header"
      />

      <div className="flex flex-wrap items-center gap-2 fade-up">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="bg-[#111827] border border-[#1f293d] h-9" data-testid="texts-status-tabs">
            <TabsTrigger value="awaiting" data-testid="texts-tab-awaiting" className="text-xs">Awaiting <span className="ml-1.5 font-mono text-amber-300">{counts.awaiting || 0}</span></TabsTrigger>
            <TabsTrigger value="no_response_needed" data-testid="texts-tab-no-response" className="text-xs">No reply needed <span className="ml-1.5 font-mono text-slate-400">{counts.no_response_needed || 0}</span></TabsTrigger>
            <TabsTrigger value="resolved" data-testid="texts-tab-resolved" className="text-xs">Resolved <span className="ml-1.5 font-mono text-emerald-300">{counts.resolved || 0}</span></TabsTrigger>
            <TabsTrigger value="all" data-testid="texts-tab-all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="texts-tab-alerts" className="text-xs"><BellRing className="h-3 w-3 mr-1" /> Alert log</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <Input data-testid="texts-search-input" placeholder="Search number, message or owner…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pl-8 text-xs bg-[#0f172a] border-[#1f293d]" />
        </div>
        {isAdmin && <FilterSelect testId="texts-filter-agent" placeholder="Owners" value={agent} onChange={setAgent} options={[{ value: "unassigned", label: "Unassigned (fallback)" }, ...(meta.data?.agents || []).map((a) => ({ value: a.id, label: a.name }))]} />}
      </div>

      {status === "alerts" ? (
        <Section padded={false} testId="alerts-log">
          {alerts.isLoading ? <Loading /> : alerts.data?.length ? (
            <ul className="divide-y divide-[#1f293d]">
              {alerts.data.map((a) => (
                <li key={a.id} className="px-4 py-3 flex flex-wrap items-center gap-3 row-hover cursor-pointer" onClick={() => setParams({ thread: a.thread_id })} data-testid={`alert-row-${a.id}`}>
                  <Pill tone={a.level > 1 ? "red" : "amber"}>Alert #{a.level}</Pill>
                  <span className="font-mono text-sm text-slate-100">{fmtPhone(a.client_number)}</span>
                  <span className="text-xs text-slate-400 truncate max-w-md">“{a.message_text}”</span>
                  <span className="text-xs text-slate-300 ml-auto">{a.is_fallback ? <span className="text-red-300">fallback → {a.to}</span> : <>{a.responsible_name} · {a.to}</>}{a.cc?.length ? <span className="text-slate-500"> · cc {a.cc.length}</span> : ""}</span>
                  <span className="font-mono text-xs text-slate-500">{a.minutes_waiting}m · {relTime(a.sent_at)}</span>
                  <Pill tone={a.email_status === "sent" ? "emerald" : "red"}>email</Pill>
                  <Pill tone={a.slack_status === "sent" ? "emerald" : "slate"}>slack</Pill>
                </li>
              ))}
            </ul>
          ) : <EmptyState icon={ShieldCheck} title="No alerts sent" hint="SLA alerts will be logged here with their email and Slack delivery status." />}
        </Section>
      ) : (
        <Section padded={false} testId="threads-list">
          {threads.isLoading ? <Loading /> : threads.data?.items?.length ? (
            <ul className="divide-y divide-[#1f293d]">
              {threads.data.items.map((t) => (
                <li key={t.id} className="px-4 py-3 flex items-start gap-3 row-hover cursor-pointer" onClick={() => setParams({ thread: t.id })} data-testid={`thread-row-${t.id}`}>
                  <span className={cn("mt-1.5 h-2 w-2 rounded-full shrink-0", t.status === "awaiting" ? (t.alert_count ? "bg-red-400 pulse-dot text-red-400" : "bg-amber-400") : t.status === "resolved" ? "bg-emerald-400" : "bg-slate-600")} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm text-slate-100">{fmtPhone(t.client_number)}</span>
                      <span className="text-[11px] text-slate-500">via {t.phone_number_name || fmtPhone(t.phone_number)}</span>
                      <Pill tone={STATUS_META[t.status]?.tone}>{STATUS_META[t.status]?.label || t.status}</Pill>
                      {t.status === "awaiting" && <LiveTimer since={t.awaiting_since} className="text-xs text-amber-300" />}
                      {t.alert_count > 0 && <Pill tone="red">{t.alert_count} alert{t.alert_count > 1 ? "s" : ""}</Pill>}
                    </div>
                    <div className="text-sm text-slate-300 truncate mt-0.5">“{t.last_message_text}”</div>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500 mt-1">
                      <span>{fmtShort(t.last_message_at)}</span>
                      <span>·</span>
                      {t.responsible_name ? <span className="text-blue-300">Owner: {t.responsible_name}</span> : <span className="text-red-300 inline-flex items-center gap-1"><UserX className="h-3 w-3" /> No prior team contact → fallback</span>}
                      {t.classification?.reason && <span className="hidden sm:inline">· {t.classification.reason}</span>}
                      {t.response_minutes != null && <span className="text-emerald-300">· {t.resolution === "call" ? "called back" : "replied"} in {fmtMinutes(t.response_minutes)}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={MessageSquare} title={status === "awaiting" ? "No clients waiting" : "Nothing here"} hint="Threads update every 5 minutes from OpenPhone and instantly via webhooks." testId="threads-empty" />
          )}
        </Section>
      )}

      <ThreadDrawer id={openId} onClose={() => setParams({})} />
    </>
  );
}
